import { useEffect } from "react";

/**
 * Injects a JSON-LD structured-data block into <head> for the lifetime of the
 * component. Pass any schema.org payload (Product, Article, BreadcrumbList, ...).
 * You can pass an array to register multiple schemas at once.
 */
export const useJsonLd = (data: Record<string, unknown> | Array<Record<string, unknown>>) => {
  // Stable key so we don't re-render on every keystroke; data is expected to be a literal per page.
  const json = JSON.stringify(data);
  useEffect(() => {
    // Emit one <script> per schema object so that crawlers (Google Search Console
    // in particular) parse each block independently. A single script containing a
    // top-level JSON array is technically valid but has historically caused
    // "missing field" false positives in Google's Rich Results parser.
    const payloads = Array.isArray(data) ? data : [data];
    const scripts = payloads.map((payload) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.text = JSON.stringify(payload);
      script.dataset.dynamic = "true";
      document.head.appendChild(script);
      return script;
    });
    return () => {
      scripts.forEach((s) => {
        if (s.parentNode) s.parentNode.removeChild(s);
      });
    };
  }, [json]);
};